import {
	ChangeDetectionStrategy,
	Component,
	computed,
	inject,
	input,
	output,
	signal,
} from '@angular/core';
import { STATUSES } from '@models/statuses';
import { select } from '@ngxs/store';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { BUTTON_PRESETS, ButtonComponent } from '@ui/button/button.component';
import { PageBarComponent } from '@ui/page-bar/page-bar.component';
import { StatusComponent } from '@ui/status/status.component';
import { ESigningStatusActionsDropdownComponent } from '../../common/e-signing-status-actions-dropdown/e-signing-status-actions-dropdown.component';
import { EnvelopeService } from '@services/envelope/envelope.service';
import { UpdateEnvelopeRequest } from '@models/e-signing';
import { Logger } from '@core/logger';
import { BusinessFeatureFlagSelectors } from '@state/business';
import { NgIf } from '@angular/common';

@Component({
	selector: 'app-e-signing-page-header',
	standalone: true,
	imports: [
		PageBarComponent,
		ButtonComponent,
		StatusComponent,
		ESigningStatusActionsDropdownComponent,
		NgIf,
	],
	templateUrl: './e-signing-page-header.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ESigningPageHeaderComponent {
	private envelopeService = inject(EnvelopeService);
	private matterId_ = select(SelectedMatter.id);
	public status = input<STATUSES>();
	public envelopeName = input<string>();
	public envelopeId = input<string>();
	public envelopeNameChange = output<string>();
	public editEnvelopeName = signal<boolean>(false);
	protected signItUpdateFlowFeatureFlagEnabled_ = select(
		BusinessFeatureFlagSelectors.sc26438EnableSignItUpdateFlow,
	);
	protected updateActionButton = computed(() => {
		switch (this.status()) {
			case STATUSES.LOADING:
				return {
					text: 'Loading...',
					color: 'gray',
					preset: BUTTON_PRESETS.EXTRA_LOW_EMPHASIS,
					loading: true,
				};
			default:
				return {
					text: 'Update',
					icon: 'question',
					color: 'secondary',
					preset: BUTTON_PRESETS.HIGH_EMPHASIS,
				};
		}
	});
	public primaryActionButton = computed(() => {
		switch (this.status()) {
			case STATUSES.DRAFT:
				return {
					text: 'Send',
					icon: 'send',
					color: 'primary',
					preset: BUTTON_PRESETS.HIGH_EMPHASIS,
				};
			case STATUSES.IN_PROGRESS:
			case STATUSES.SENT_ALT:
				return {
					text: 'Cancel',
					icon: 'mail-x',
					color: 'gray',
					preset: BUTTON_PRESETS.EXTRA_LOW_EMPHASIS,
				};
			case STATUSES.COMPLETE:
				return {
					text: 'Download',
					icon: 'download',
					color: 'primary',
					preset: BUTTON_PRESETS.HIGH_EMPHASIS,
				};
			case STATUSES.LOADING:
				return {
					text: 'Loading...',
					color: 'gray',
					preset: BUTTON_PRESETS.EXTRA_LOW_EMPHASIS,
					loading: true,
				};
			default:
				return {
					text: 'Unknown Action',
					icon: 'question',
					color: 'primary',
					preset: BUTTON_PRESETS.HIGH_EMPHASIS,
				};
		}
	});

	goBack() {
		this.envelopeService.goToEnvelopesView(this.matterId_());
	}

	async updateActionButtonClick() {
		try {
			const envelopeId = this.envelopeId();
			const request: UpdateEnvelopeRequest = {
				// Once we have the UX, we can move this to a modal if needed.
				note: 'This note is hardcoded until we have the UX ready.',
				isSignatureDrawOnly: false,
			};

			await this.envelopeService.updateEnvelope(
				this.matterId_(),
				envelopeId,
				request,
			);
		} catch (error) {
			Logger.exception(error, { msg: 'Error updating the envelope.' });
		}
	}

	primaryActionButtonClick() {
		const envelopeId = this.envelopeId();
		switch (this.status()) {
			case STATUSES.DRAFT:
				return this.envelopeService.openSendEnvelopeModal(
					this.matterId_(),
					envelopeId,
				);
			case STATUSES.IN_PROGRESS:
			case STATUSES.SENT_ALT:
				return this.envelopeService.openPopConfirmForCancel(
					this.matterId_(),
					envelopeId,
				);
			case STATUSES.COMPLETE:
				return this.envelopeService.downloadEnvelope(envelopeId);
			default:
				return;
		}
	}

	onRenameClick() {
		this.editEnvelopeName.set(true);
	}

	onEnvelopeNameChange(newName: string) {
		this.editEnvelopeName.set(false);
		this.envelopeNameChange.emit(newName);
	}
}
